import React, { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import LeafletMap from '../../components/LeafletMap'
import { FleetTabs } from '../../components/features/provider/FleetTabs'
import DroneStationCreatePanel from '../../components/features/provider/DroneStationCreatePanel'
import { Body } from '../../components/layout/Body'
import { Title } from '../../components/layout/Title'
import { Page } from '../../components/ui/Page'
import Store from '../../store/Store'
import { IconSearch } from '../../components/Icons';
import DroneStationDropdownCard, {
    type DroneStationEditPayload,
    type DroneStationItem,
} from '../../components/features/provider/DroneStationDropdownCard'

interface GeoFeature {
    id?: string
    _selected?: boolean
    properties: { areaBasePrice?: number; decisionFee?: number; applicationFee?: number }
}

interface GeoMesh {
    uuid: string
    name: string
    created?: string
    updated?: string
    droneName?: string
    droneStationLatitude?: number
    droneStationLongitude?: number
    droneDiameter?: number
    serialNumber?: string
    callsign?: string
    deviceOnlineStatus?: boolean
    modeCode?: number
    stationModel?: {
		id: number
		key: string
		domain: string
		type: string
		subType: string
		name: string
		deviceClass: string
	}
	cameras?: Array<{
		id: number
		cameraIndex: string
		cameraPosition: string
	}>
    _geo?: unknown
    droneId?: number
}

interface GeoJSON {
    _uuid?: string
    _isAllStations?: boolean
    _isViewport?: boolean
    _center?: { lat: number; lng: number }
    _name?: string
    features?: Array<{ geometry?: { coordinates?: number[][][] } }>
}

interface DroneTypeInfo {
    id: number
    name: string
    reachDiameter: number
    flightSpeed: number
    startStopTime: number
    maxFlightTime: number
}

interface Drone {
    id: number
    name: string
    serialNumber?: string
    callsign?: string
    deviceOnlineStatus?: boolean
    modeCode?: number
    droneType: DroneTypeInfo
}

interface GatewayInfo {
    sn: string
    callsign: string
    online: boolean
    modeCode?: number
}

interface GeoSetup {
    map?: {
        getBounds: () => {
            getNorthWest: () => { lat: number; lng: number }
            getSouthEast: () => { lat: number; lng: number }
        }
    }
    onSaveGeo?: (values: (number | string)[]) => void
}

export default function DroneStationsPage() {
    const mode = Store.use('mode')
    const [searchTerm, setSearchTerm] = useState('')
    const [activeStationUuid, setActiveStationUuid] = useState<string | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [newStationName, setNewStationName] = useState('')
    const [selectedGatewaySn, setSelectedGatewaySn] = useState('')
    const [createValues, setCreateValues] = useState<number[]>([100, 0, 0])
    const [features, geoJSON, geoMeshes, geoMode, geoCenter, snappedCenter, drones, selectedDrone, availableGateways] = Store.use(
        'features',
        'geoJSON',
        'geoMeshes',
        'geoMode',
        'geoCenter',
        'snappedCenter',
        'drones',
        'selectedDrone',
        'availableGateways'
    ) as [
        Record<string, GeoFeature>,
        GeoJSON | null,
        GeoMesh[] | undefined,
        string,
        { lat: number; lng: number } | null,
        { lat: number; lng: number } | null,
        Drone[] | undefined,
        { id: number } | null,
        GatewayInfo[] | undefined,
    ]

    const geoSetup = useMemo<GeoSetup>(() => {
        Store.loadStations()
        Store.loadDrones()
        Store.loadAvailableGateways()
        return {}
    }, [])

    useEffect(() => {
        Store.set({ geoMode: 'edit' })
        Store.loadAllStationsCells()
    }, [])

    function getCenter(geo: GeoJSON): { lat: number; lng: number } | null {
        if (!geo?.features?.length) return null
        const coos = geo.features[0]?.geometry?.coordinates?.[0]
        if (!coos?.length || !coos[0] || !coos[3]) return null
        return {
            lat: (coos[0][1] + coos[3][1]) / 2,
            lng: (coos[0][0] + coos[3][0]) / 2,
        }
    }

    function handleShowAllStations() {
        setActiveStationUuid(null)
        Store.set({ features: {}, geoCenter: null })
        Store.loadAllStationsCells()
    }

    function handleSelectStation(station: DroneStationItem) {
        setActiveStationUuid(station.uuid)
        const mesh = station as GeoMesh
        Store.set({ features: {}, geoCenter: null })

        if (!mesh._geo) {
            Store.fetch('provider/station/' + mesh.uuid).then((result: { geoJson?: string; centerLatitude?: number; centerLongitude?: number }) => {
                if (!result?.geoJson) return

                const geo = JSON.parse(result.geoJson) as GeoJSON
                if (result.centerLatitude != null && result.centerLongitude != null) {
                    geo._center = { lat: result.centerLatitude, lng: result.centerLongitude }
                } else {
                    geo._center = getCenter(geo) ?? undefined
                }

                geo._name = mesh.name
                geo._uuid = mesh.uuid
                geo._isAllStations = false
                geo._isViewport = false

                ;(mesh as GeoMesh & { _geo?: GeoJSON })._geo = geo
                Store.set('geoJSON', geo)
            })
            return
        }

        const geo = mesh._geo as GeoJSON | undefined
        if (!geo) return
        Store.set('geoJSON', geo)
    }

    function handleToggleStation(station: DroneStationItem) {
        if (activeStationUuid === station.uuid) {
            handleShowAllStations()
            return
        }
        handleSelectStation(station)
    }

    const stations = (geoMeshes ?? []) as DroneStationItem[]
    const filteredStations = stations.filter((station) => {
        const q = searchTerm.trim().toLowerCase()
        if (!q) return true
        const haystack = [
            station.name,
            station.callsign,
            station.serialNumber,
            station.droneName,
        ].join(' ').toLowerCase()
        return haystack.includes(q)
    })

    useEffect(() => {
        if (!activeStationUuid) return
        const stillVisible = filteredStations.some((station) => station.uuid === activeStationUuid)
        if (!stillVisible) {
            handleShowAllStations()
        }
    }, [activeStationUuid, filteredStations])

    if (mode !== 'PROVIDER') {
        return <Navigate to="/dashboard" replace />
    }
function openCreate() {
        setIsCreating(true)
        setActiveStationUuid(null)
        setNewStationName('')
        setSelectedGatewaySn('')
        setCreateValues([100, 0, 0])
        Store.set({
            geoMode: 'create',
            geoJSON: null,
            geoCenter: null,
            snappedCenter: null,
            selectedDrone: null,
            features: {},
        })
    }

    function cancelCreate() {
        setIsCreating(false)
        setNewStationName('')
        setSelectedGatewaySn('')
        setCreateValues([100, 0, 0])
        Store.set({ geoMode: 'edit', geoCenter: null, snappedCenter: null })
        handleShowAllStations()
    }

    async function saveCreate() {
        const centerToUse = snappedCenter || geoCenter
        if (!centerToUse || !newStationName.trim() || !selectedGatewaySn) return

        await Store.createStation(
            newStationName.trim(),
            selectedGatewaySn,
            centerToUse,
            selectedDrone?.id,
            geoCenter,
            {
                areaBasePrice: createValues[0],
                decisionFee: createValues[1],
                applicationFee: createValues[2],
            }
        )

        setIsCreating(false)
        setNewStationName('')
        setSelectedGatewaySn('')
        setCreateValues([100, 0, 0])
        setActiveStationUuid(null)
        Store.set({ geoMode: 'edit' })
        Store.loadAllStationsCells()
    }

    function handleDeleteStation(station: DroneStationItem) {
        Store.deleteStation(station.uuid)
    }

    async function handleEditStation(station: DroneStationItem, payload: DroneStationEditPayload) {
        await Store.moveStation(station.uuid, payload.latitude, payload.longitude)
    }
    return (
        <Page>
            <Body>
                <div className="flex mb-6 items-center gap-8">
                    <Title>Flottenmanagement</Title>
                    <FleetTabs section="stations" />
                </div>

				<div
					className="h-px bg-[#B8D2EB]"
					style={{
						width: '100vw',
						marginLeft: 'calc(50% - 50vw)',
					}}
				/>

                <div
                    className="mt-8 flex w-full items-stretch gap-12"
                >
                    <div
                        className="flex w-[48%] max-w-[720px] flex-col p-4 pb-0"
                    >
                        <div className="mb-6 flex items-center gap-6">
                            <label className="fleet-search-field flex h-12 flex-1 items-center gap-3 rounded-full border border-[#333333] bg-white px-5">
                                <span className="sr-only">Drohnenstationen suchen</span>
                                <IconSearch />
                                <input
                                    type="search"
                                    aria-label="Drohnenstationen suchen"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Suche"
                                    className="fleet-search-input min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[#8D8C8C]"
                                />
                            </label>

                            <button
                                type="button"
                                onClick={openCreate}
                                className="h-12 shrink-0 rounded-full bg-[#E1320F] px-8 text-sm font-semibold text-white transition hover:bg-[#c62c0d]"
                            >
                                Station hinzufügen
                            </button>
                        </div>

                        {isCreating ? (
                            <DroneStationCreatePanel
                                name={newStationName}
                                onNameChange={setNewStationName}
                                selectedGatewaySn={selectedGatewaySn}
                                onGatewayChange={setSelectedGatewaySn}
                                availableGateways={availableGateways}
                                latitude={geoCenter?.lat != null ? String(geoCenter.lat) : ''}
                                longitude={geoCenter?.lng != null ? String(geoCenter.lng) : ''}
                                onLatitudeChange={(value) => {
                                    const lat = parseFloat(String(value))
                                    if (Number.isNaN(lat)) return
                                    const center = { lat, lng: geoCenter?.lng ?? 15.0 }
                                    Store.set('geoCenter', center)
                                    Store.snapToGrid(center.lat, center.lng)
                                }}
                                onLongitudeChange={(value) => {
                                    const lng = parseFloat(String(value))
                                    if (Number.isNaN(lng)) return
                                    const center = { lat: geoCenter?.lat ?? 48.0, lng }
                                    Store.set('geoCenter', center)
                                    Store.snapToGrid(center.lat, center.lng)
                                }}
                                basePrice={createValues[0]}
                                decisionFee={createValues[1]}
                                applicationFee={createValues[2]}
                                onBasePriceChange={(value) =>
                                    setCreateValues([parseInt(String(value), 10) || 0, createValues[1], createValues[2]])
                                }
                                onDecisionFeeChange={(value) =>
                                    setCreateValues([createValues[0], parseInt(String(value), 10) || 0, createValues[2]])
                                }
                                onApplicationFeeChange={(value) =>
                                    setCreateValues([createValues[0], createValues[1], parseInt(String(value), 10) || 0])
                                }
                                onCancel={cancelCreate}
                                onSave={saveCreate}
                                saveEnabled={newStationName.trim().length > 0 && selectedGatewaySn.length > 0 && geoCenter !== null}
                            />
                        ) : (
                            <div
                                className="min-h-0 flex-1 pr-4"
                                tabIndex={0}
                                aria-label="Drohnenliste"
                            >
                                <div className="flex flex-col gap-4 pb-6">
                                    {filteredStations.length > 0 ? (
                                        filteredStations.map((station) => (
                                            <DroneStationDropdownCard
                                                key={station.uuid}
                                                station={station}
                                                onDelete={handleDeleteStation}
                                                onEdit={handleEditStation}
                                                expanded={activeStationUuid === station.uuid}
                                                onToggle={handleToggleStation}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-sm text-gray-500">Keine Drohnenstationen vorhanden.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        className={
                            'order-page-right w-full max-w-[600px] pr-1 pb-6'
                        }
                    >
                        <div style={{ aspectRatio: '1 / 1', width: '100%', maxWidth: 600, minWidth: 300 }}>
                            <LeafletMap
                                geoSearch={isCreating}
                                editGeo={geoSetup as unknown as Record<string, unknown>}
                            />
                        </div>
                    </div>
                </div>
            </Body>
        </Page>
    )
}
